import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  FormGroup,
  Checkbox,
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import usePageTitle from '../../utils/usePageTitle';

const Reports = () => {
  usePageTitle('Reports & Analytics');
  const [projectType, setProjectType] = useState(''); // C=Carson, Q=QA, P=PSA
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportTypes, setReportTypes] = useState({
    crf: false,  // Customer Request Form
    dr: false,   // Delivery Request
    rn: false,   // Release Notice
    wo: false,   // Work Order
    di: false,   // Complete Invoice (Detail Invoice)
    bi: false,   // Billing Invoice
    ef: false    // Effort
  });
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleProjectTypeChange = (event) => {
    setProjectType(event.target.value);
    setError('');
    setFieldErrors(prev => ({ ...prev, projectType: false }));
  };

  const handleReportTypeChange = (event) => {
    const { name, checked } = event.target;
    setReportTypes(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
    setFieldErrors(prev => ({ ...prev, reportTypes: false }));
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    setReportTypes({
      crf: checked,
      dr: checked,
      rn: checked,
      wo: checked,
      di: checked,
      bi: checked,
      ef: checked
    });
    setError('');
    setFieldErrors(prev => ({ ...prev, reportTypes: false }));
  };

  const validateForm = () => {
    const errors = [];

    if (!projectType) {
      errors.push('Please select Department');
    }

    if (!startDate && !endDate) {
      errors.push('Start and End Date should be given');
    } else if (!startDate) {
      errors.push('Start Date should be given');
    } else if (!endDate) {
      errors.push('End Date should be given');
    }

    const hasSelectedReports = Object.values(reportTypes).some(selected => selected);
    if (!hasSelectedReports) {
      errors.push('Select any Report');
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      errors.push('Start date must be before end date');
    }

    return errors;
  };

  const handleGenerateReport = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setFieldErrors({
        projectType: !projectType,
        startDate: !startDate,
        endDate: !endDate,
        reportTypes: !Object.values(reportTypes).some(selected => selected)
      });
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Format dates to MM/DD/YYYY as expected by backend
      const formatDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-');
        return `${month}/${day}/${year}`;
      };

      // Build request data matching backend expectations
      const requestData = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        projectType: projectType,
        // Send individual report type flags as backend expects
        crf: reportTypes.crf ? 'CRF' : '',
        dr: reportTypes.dr ? 'DR' : '',
        rn: reportTypes.rn ? 'RN' : '',
        wo: reportTypes.wo ? 'WO' : '',
        di: reportTypes.di ? 'DI' : '',
        bi: reportTypes.bi ? 'BI' : '',
        ef: reportTypes.ef ? 'EF' : '',
        // Also send as array for alternative parsing
        reportTypes: Object.entries(reportTypes)
          .filter(([_, selected]) => selected)
          .map(([type, _]) => type.toUpperCase())
      };

      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      
      const response = await axios.post(`${API_ENDPOINTS.BASE_URL}/reports/generate`, requestData, {
        responseType: 'text',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Decode base64 to binary
      const binaryString = atob(response.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Carson_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Report generated and downloaded successfully!');

    } catch (error) {
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Generate Reports
      </Typography>
      
      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          role="alert"
          aria-live="assertive"
        >
          {error}
        </Alert>
      )}

      {/* Success Display */}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          role="status"
          aria-live="polite"
        >
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3} sx={{ width: '100%' }}>
          {/* Department Selection */}
          <Grid item xs={12} md={3}>
            <FormControl component="fieldset" error={fieldErrors.projectType}>
              <FormLabel 
                component="legend" 
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 1 }}
                id="department-label"
              >
                Department *
              </FormLabel>
              <RadioGroup
                aria-labelledby="department-label"
                value={projectType}
                onChange={handleProjectTypeChange}
              >
                <FormControlLabel 
                  value="C" 
                  control={<Radio inputProps={{ 'aria-label': 'Carson Location' }} />} 
                  label="Carson Location" 
                />
                <FormControlLabel 
                  value="Q" 
                  control={<Radio inputProps={{ 'aria-label': 'QA Location' }} />} 
                  label="QA Location" 
                />
                <FormControlLabel 
                  value="P" 
                  control={<Radio inputProps={{ 'aria-label': 'PSA Location' }} />} 
                  label="PSA Location" 
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography 
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 1 }}
                component="h2"
              >
                Date Range *
              </Typography>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setError('');
                  setFieldErrors(prev => ({ ...prev, startDate: false }));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  'aria-label': 'Start Date',
                  'aria-required': 'true',
                  'aria-invalid': fieldErrors.startDate ? 'true' : 'false'
                }}
                error={fieldErrors.startDate}
                required
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setError('');
                  setFieldErrors(prev => ({ ...prev, endDate: false }));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  'aria-label': 'End Date',
                  'aria-required': 'true',
                  'aria-invalid': fieldErrors.endDate ? 'true' : 'false'
                }}
                error={fieldErrors.endDate}
                required
                fullWidth
              />
            </Box>
          </Grid>

          {/* Report Types */}
          <Grid item xs={12} md={3}>
            <FormControl component="fieldset" error={fieldErrors.reportTypes}>
              <FormLabel 
                component="legend" 
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 1 }}
                id="report-types-label"
              >
                Report Types *
              </FormLabel>
              <FormGroup aria-labelledby="report-types-label">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={
                        Object.values(reportTypes).some(Boolean) && 
                        !Object.values(reportTypes).every(Boolean)
                      }
                      inputProps={{ 'aria-label': 'Select all report types' }}
                    />
                  }
                  label="Check All"
                  sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportTypes.crf}
                      onChange={handleReportTypeChange}
                      name="crf"
                      inputProps={{ 'aria-label': 'Customer Request Form' }}
                    />
                  }
                  label="Customer Request Form"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportTypes.dr}
                      onChange={handleReportTypeChange}
                      name="dr"
                      inputProps={{ 'aria-label': 'Delivery Request' }}
                    />
                  }
                  label="Delivery Request"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportTypes.rn}
                      onChange={handleReportTypeChange}
                      name="rn"
                      inputProps={{ 'aria-label': 'Release Notice' }}
                    />
                  }
                  label="Release Notice"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportTypes.wo}
                      onChange={handleReportTypeChange}
                      name="wo"
                      inputProps={{ 'aria-label': 'Work Order' }}
                    />
                  }
                  label="Work Order"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportTypes.di}
                      onChange={handleReportTypeChange}
                      name="di"
                      inputProps={{ 'aria-label': 'Complete Invoice' }}
                    />
                  }
                  label="Complete Invoice"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportTypes.bi}
                      onChange={handleReportTypeChange}
                      name="bi"
                      inputProps={{ 'aria-label': 'Billing Invoice' }}
                    />
                  }
                  label="Billing Invoice"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportTypes.ef}
                      onChange={handleReportTypeChange}
                      name="ef"
                      inputProps={{ 'aria-label': 'Effort' }}
                    />
                  }
                  label="Effort"
                />
              </FormGroup>
            </FormControl>
          </Grid>

          {/* Generate Button */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%', pb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateReport}
                size="large"
                disabled={loading}
                fullWidth
                aria-label="Generate Reports"
              >
                {loading ? (
                  <>
                    <CircularProgress 
                      size={20} 
                      sx={{ mr: 1 }} 
                      aria-label="Loading"
                    />
                    Generating...
                  </>
                ) : (
                  'Generate Reports'
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Reports;